import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, X, Loader2 } from 'lucide-react';
import { getItems } from '../utils/itemApi';
import { getDropdownPosition } from '../utils/dropdownPosition';
import type { Item, ItemType } from '../types/item';

const formatItemType = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

interface AsyncItemSelectProps {
  value: number;
  selectedLabel?: string;
  onSelect: (item: Item) => void;
  onClear: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  /** When set, only items of this type are returned from search. */
  itemType?: ItemType;
}

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 25;

const AsyncItemSelect = ({
  value,
  selectedLabel = '',
  onSelect,
  onClear,
  placeholder = 'Select item',
  searchPlaceholder = 'Search by name or SKU...',
  className = '',
  disabled = false,
  itemType,
}: AsyncItemSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const keyboardFocusRef = useRef(false);

  const isEmpty = !value || value === 0;
  const displayLabel = !isEmpty ? selectedLabel || `Item #${value}` : '';

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    setDropdownStyle(getDropdownPosition(containerRef.current.getBoundingClientRect()));
  }, []);

  const fetchItems = useCallback(async (query: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const trimmed = query.trim();
      const result = await getItems({
        search: trimmed || undefined,
        active: true,
        item_type: itemType,
        per_page: PAGE_SIZE,
        page: 1,
      });

      if (requestId !== requestIdRef.current) return;
      setResults(result.items);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setResults([]);
      setError(err instanceof Error ? err.message : 'Failed to search items');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [itemType]);

  useEffect(() => {
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') keyboardFocusRef.current = true;
    };
    const handlePointerDown = () => {
      keyboardFocusRef.current = false;
    };
    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();
    inputRef.current?.focus({ preventScroll: true });
    requestAnimationFrame(updateDropdownPosition);

    const handleReposition = () => updateDropdownPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchItems(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isOpen, search, fetchItems]);

  const handleOpen = (initialSearch = '') => {
    if (disabled) return;
    setIsOpen(true);
    setSearch(initialSearch);
    setResults([]);
    setError(null);
  };

  const handleSelect = (item: Item) => {
    onSelect(item);
    setIsOpen(false);
    setSearch('');
    setResults([]);
  };

  const handleClear = (e: ReactMouseEvent) => {
    e.stopPropagation();
    onClear();
    setSearch('');
    setResults([]);
  };

  const handleTriggerFocus = () => {
    if (disabled || isOpen) return;
    if (keyboardFocusRef.current) {
      keyboardFocusRef.current = false;
      handleOpen();
    }
  };

  const handleTriggerKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleOpen();
      return;
    }

    if (!isOpen && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      handleOpen(e.key);
    }
  };

  const listMessage = () => {
    if (loading) return null;
    if (error) return error;
    if (results.length === 0) {
      return search.trim()
        ? 'No items found'
        : 'Type to search, or pick from the list below';
    }
    return null;
  };

  const message = listMessage();

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden flex flex-col"
    >
      <div className="p-2 border-b border-gray-100 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800 focus:border-slate-600"
          />
        </div>
      </div>

      <ul className="overflow-y-auto py-1 flex-1 min-h-0">
        {loading && (
          <li className="px-3 py-3 text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            Searching...
          </li>
        )}
        {!loading && message && (
          <li className={`px-3 py-2 text-sm ${error ? 'text-red-600' : 'text-gray-500'}`}>{message}</li>
        )}
        {!loading &&
          results.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => handleSelect(item)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                  item.id === value ? 'bg-slate-50 text-slate-700 font-medium' : 'text-gray-900'
                }`}
              >
                <span className="block truncate">{item.name}</span>
                <span className="block text-xs text-gray-500 truncate">
                  {item.sku} · {formatItemType(item.item_type)}
                </span>
              </button>
            </li>
          ))}
      </ul>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => (isOpen ? setIsOpen(false) : handleOpen())}
        onFocus={handleTriggerFocus}
        onKeyDown={handleTriggerKeyDown}
        className={`w-full flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm bg-white text-left transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-slate-600 ${
          disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : 'hover:border-slate-400'
        }`}
      >
        <span className={isEmpty ? 'text-gray-400 truncate' : 'text-gray-900 truncate'}>
          {isEmpty ? placeholder : displayLabel}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isEmpty && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onClear();
                  setSearch('');
                }
              }}
              className="p-0.5 text-gray-400 hover:text-gray-600 rounded"
              aria-label="Clear selection"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
};

export default AsyncItemSelect;
